import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
    UserIcon,
    MailIcon,
    PhoneIcon,
    BriefcaseIcon,
    CalendarIcon,
    DownloadIcon,
    ChevronLeftIcon,
    EditIcon,
    TrashIcon,
    ClockIcon,
    MapPinIcon,
    LinkIcon,
    PlusIcon
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { Button, Space, Card, Tag, Popconfirm, message, Modal } from 'antd';
import { CalendarOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ScheduleEventModal from '../components/ScheduleEventModal';
import EditEventModal from '../components/EditEventModal';
import { API_BASE_URL } from "../config/apiconfig";


const CandidateDetails = () => {
    const { email } = useParams();
    const [candidate, setCandidate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [recruiterId, setRecruiterId] = useState(null);
    const [eventModalVisible, setEventModalVisible] = useState(false);
    const [editEventModalVisible, setEditEventModalVisible] = useState(false);
    const [scheduledEvents, setScheduledEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [resumeUrl, setResumeUrl] = useState(null);
    const [resumeName, setResumeName] = useState('');
    const [resumeError, setResumeError] = useState(false);
    const [resumeTimestamp, setResumeTimestamp] = useState(null);
    const [gatePassVisible, setGatePassVisible] = useState(false);
    const [gatePassHtml, setGatePassHtml] = useState('');
    const [gatePassLoading, setGatePassLoading] = useState(false);


    const handleResumeError = () => {
        setResumeError(true);
    };

    useEffect(() => {
    const fetchCandidate = async () => {
        try {
            setLoading(true);
            setError(null);
            if (!email) throw new Error("No candidate email provided");
            const response = await fetch(`${API_BASE_URL}/profile/${email}`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            
            // Fetch the application ID for this candidate
            const appResponse = await fetch(`${API_BASE_URL}/api/applications/candidate/${encodeURIComponent(email)}`);
            const applications = await appResponse.json();
            const applicationId = applications.length > 0 ? applications[0].id : null;

            // Set resume URL and name if resume exists
            if (data.resume) {
                setResumeUrl(`${API_BASE_URL}/profile/${email}/resume`);
                setResumeName(data.resume_name || `${data.firstName || 'Candidate'}_${data.lastName || 'Resume'}.pdf`); 
                // Set timestamp only once when component loads
                setResumeTimestamp(Date.now());
            }

            setCandidate({
                ...data,
                applicationId,
                firstName: data.firstName || "",
                lastName: data.lastName || "",
                phoneCode: data.phoneCode || "",
                phoneNumber: data.phoneNumber || "",
                summary: data.summary || "No summary provided",
                applyingPosition: data.applyingPosition || "No position specified",
                skills: data.skills || [],
                careers: data.careers || [],
                education: data.education || [],
                licenses: data.licenses || [],
            });
        } catch (error) {
            console.error("Error fetching candidate:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    fetchCandidate();
}, [email]);

    useEffect(() => {
        const fetchRecruiterId = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('user'));
                if (user && user.id) {
                    setRecruiterId(user.id);
                }
            } catch (error) {
                console.error('Error fetching recruiter ID:', error);
            }
        };
        fetchRecruiterId();
    }, []);

        const renderResumePreview = () => {
        if (!candidate?.resume || resumeError || !resumeTimestamp) return null;

        return (
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Resume Preview</h3>
                    <div className="border rounded-md shadow p-4 bg-white" style={{ height: '500px' }}>
                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                            <Viewer
                                fileUrl={`${API_BASE_URL}/profile/${email}/resume?t=${resumeTimestamp}`}
                                plugins={[defaultLayoutPluginInstance]}
                                onError={handleResumeError}
                            />
                        </Worker>
                        <a
                            href={`${API_BASE_URL}/profile/${email}/resume`}
                            download={`${candidate.firstName || 'Candidate'}_${candidate.lastName || 'Resume'}.pdf`}
                            className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                        >
                            <DownloadIcon className="w-5 h-5 mr-1" />
                            Download Resume
                        </a>
                    </div>
                </div>
            </div>
        );
    };


    const fetchEvents = async () => {
        if (!candidate?.applicationId) return;
        
        setEventsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/applications/${candidate.applicationId}/events`);
            if (!response.ok) throw new Error('Failed to fetch events');
            const data = await response.json();
            setScheduledEvents(data);
        } catch (error) {
            console.error('Error fetching events:', error);
            message.error('Failed to load scheduled events');
        } finally {
            setEventsLoading(false);
        }
    };

    useEffect(() => {
        if (candidate?.applicationId) {
            fetchEvents();
        }
    }, [candidate]);

    const handleEventScheduled = (success) => {
        setEventModalVisible(false);
        if (success) {
            fetchEvents();
            message.success('Event scheduled successfully!');
        }
    };

    const handleEventUpdated = (success) => {
        setEditEventModalVisible(false);
        setSelectedEvent(null);
        if (success) {
            fetchEvents();
            message.success('Event updated successfully!');
        }
    };

    const handleEditEvent = (event) => {
        setSelectedEvent(event);
        setEditEventModalVisible(true);
    };

    const handleDeleteEvent = async (eventId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/events/${eventId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to delete event');
            }

            message.success('Event deleted successfully!');
            fetchEvents();
        } catch (error) {
            console.error('Error deleting event:', error);
            message.error('Failed to delete event');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Present";
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? "Invalid date" : date.toLocaleDateString();
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "Invalid date";
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? "Invalid date" : date.toLocaleString();
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'scheduled':
                return 'blue';
            case 'completed':
                return 'green';
            case 'cancelled':
                return 'red';
            case 'in-progress':
                return 'orange';
            default:
                return 'default';
        }
    };

    const defaultLayoutPluginInstance = defaultLayoutPlugin();

    const openGatePassModal = async (eventId) => {
        if (!candidate?.applicationId) return;
        setGatePassLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/gatepass/${candidate.applicationId}?eventId=${eventId}`, {
                headers: { 'Accept': 'text/html' }
            });
            const html = await res.text();
            setGatePassHtml(html);
            setGatePassVisible(true);
        } catch (e) {
            console.error('Failed to load gate pass:', e);
            message.error('Failed to generate gate pass');
        } finally {
            setGatePassLoading(false);
        }
    };

    const handlePrintGatePass = () => {
        const iframe = document.getElementById('gatepass-frame');
        if (iframe && iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                <Header />
                <div className="flex-1 p-6">
                    <div className="max-w-6xl mx-auto text-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading candidate details...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                <Header />
                <div className="flex-1 p-6">
                    <div className="max-w-6xl mx-auto text-center py-8">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                            <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Candidate</h2>
                            <p className="text-red-600">{error}</p>
                            <Link
                                to="/recruiter-dashboard"
                                className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-800"
                            >
                                <ChevronLeftIcon className="w-5 h-5 mr-1" />
                                Back to Dashboard
                            </Link>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (!candidate) {
        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                <Header />
                <div className="flex-1 p-6">
                    <div className="max-w-6xl mx-auto text-center py-8">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                            <h2 className="text-lg font-semibold text-yellow-800 mb-2">Candidate Not Found</h2>
                            <p className="text-yellow-600">The requested candidate could not be found.</p>
                            <Link
                                to="/recruiter-dashboard"
                                className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-800"
                            >
                                <ChevronLeftIcon className="w-5 h-5 mr-1" />
                                Back to Dashboard
                            </Link>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Header />
            <div className="flex-1 p-6">
                <div className="max-w-6xl mx-auto space-y-6">
                    <div className="flex items-center justify-between">
                        <Link
                            to="/recruiter-dashboard"
                            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                        >
                            <ChevronLeftIcon className="w-5 h-5 mr-1" />
                            Back to Dashboard
                        </Link>
                        <h1 className="text-2xl font-bold text-gray-900">Candidate Details</h1>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border">
                        <div className="p-6">
                            <div className="flex flex-col md:flex-row gap-6">
                                {/* Left Column */}
                                <div className="md:w-1/3 space-y-6">
                                    <div className="flex items-center space-x-4">
                                        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden">
                                            {candidate.profile_picture ? (
                                                <img
                                                    src={`${API_BASE_URL}/profile/${email}/picture`}
                                                    alt="Profile"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <UserIcon className="w-10 h-10 text-blue-600" />
                                            )}
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold">
                                                {candidate.firstName} {candidate.lastName}
                                            </h2>
                                            <p className="text-gray-600">{candidate.applyingPosition}</p>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-start">
                                            <MailIcon className="w-5 h-5 text-gray-500 mr-3 mt-0.5" />
                                            <div>
                                                <p className="text-sm text-gray-500">Email</p>
                                                <p className="text-gray-900">{candidate.email}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <PhoneIcon className="w-5 h-5 text-gray-500 mr-3 mt-0.5" />
                                            <div>
                                                <p className="text-sm text-gray-500">Phone</p>
                                                <p className="text-gray-900">
                                                    {candidate.phoneCode && candidate.phoneNumber
                                                        ? `${candidate.phoneCode} ${candidate.phoneNumber}`
                                                        : "Not provided"}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <BriefcaseIcon className="w-5 h-5 text-gray-500 mr-3 mt-0.5" />
                                            <div>
                                                <p className="text-sm text-gray-500">Current Position</p>
                                                <p className="text-gray-900">
                                                    {candidate.careers.length > 0
                                                        ? candidate.careers[0].jobPosition
                                                        : "Not specified"}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <CalendarIcon className="w-5 h-5 text-gray-500 mr-3 mt-0.5" />
                                            <div>
                                                <p className="text-sm text-gray-500">Experience</p>
                                                <p className="text-gray-900">
                                                    {candidate.careers.length > 0
                                                        ? `${candidate.careers.length} position(s)`
                                                        : "No experience listed"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Schedule Event Button */}
                                    <Button 
                                        type="primary" 
                                        icon={<CalendarOutlined />}
                                        onClick={() => setEventModalVisible(true)}
                                        className="w-full"
                                    >
                                        Schedule New Event
                                    </Button>
                                </div>

                                {/* Right Column */}
                                <div className="md:w-2/3 space-y-6">
                                    <div>
                                        <h3 className="text-lg font-semibold mb-2">Professional Summary</h3>
                                        <p className="text-gray-700 whitespace-pre-line">{candidate.summary}</p>
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold mb-2">Skills</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {candidate.skills.length > 0 ? (
                                                candidate.skills.map((skill, index) => (
                                                    <span
                                                        key={index}
                                                        className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                                                    >
                                                        {skill}
                                                    </span>
                                                ))
                                            ) : (
                                                <p className="text-gray-500">No skills listed</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold mb-2">Work Experience</h3>
                                        {candidate.careers.length > 0 ? (
                                            <div className="space-y-4">
                                                {candidate.careers.map((career, index) => (
                                                    <div
                                                        key={index}
                                                        className="border-l-2 border-blue-200 pl-4 py-1"
                                                    >
                                                        <h4 className="font-medium">{career.jobPosition}</h4>
                                                        <p className="text-gray-600">{career.companyName}</p>
                                                        <p className="text-sm text-gray-500">
                                                            {formatDate(career.startDate)} -{" "}
                                                            {career.endDate
                                                                ? formatDate(career.endDate)
                                                                : "Present"}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-gray-500">No work experience listed</p>
                                        )}
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold mb-2">Education</h3>
                                        {candidate.education.length > 0 ? (
                                            <div className="space-y-4">
                                                {candidate.education.map((edu, index) => (
                                                    <div
                                                        key={index}
                                                        className="border-l-2 border-blue-200 pl-4 py-1"
                                                    >
                                                        <h4 className="font-medium">{edu.degreeProgram}</h4>
                                                        <p className="text-gray-600">{edu.instituteName}</p>
                                                        <p className="text-sm text-gray-500">
                                                            {formatDate(edu.startDate)} -{" "}
                                                            {edu.endDate
                                                                ? formatDate(edu.endDate)
                                                                : "Present"}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-gray-500">No education listed</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Scheduled Events Section */}
                    <div className="bg-white rounded-lg shadow-sm border">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-semibold">Scheduled Events</h3>
                                <Button 
                                    type="primary" 
                                    icon={<PlusIcon className="w-4 h-4" />}
                                    onClick={() => setEventModalVisible(true)}
                                    size="small"
                                >
                                    Add Event
                                </Button>
                            </div>

                            {eventsLoading ? (
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                    <p className="mt-2 text-gray-600">Loading events...</p>
                                </div>
                            ) : scheduledEvents.length > 0 ? (
                                <div className="space-y-4">
                                    {scheduledEvents.map((event) => (
                                        <Card key={event.id} className="border-l-4 border-blue-500">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h4 className="font-medium text-lg">{event.event_name}</h4>
                                                        <Tag color={getStatusColor(event.status)}>
                                                            {event.status || 'Scheduled'}
                                                        </Tag>
                                                        <Tag color="purple">{event.event_type}</Tag>
                                                    </div>
                                                    
                                                    <div className="space-y-2 text-sm text-gray-600">
                                                        <div className="flex items-center gap-2">
                                                            <ClockIcon className="w-4 h-4" />
                                                            <span>{formatDateTime(event.scheduled_time)}</span>
                                                            <span className="text-gray-400">•</span>
                                                            <span>{event.duration || 60} minutes</span>
                                                        </div>
                                                        
                                                        {event.event_type === 'Virtual' && event.event_url && (
                                                            <div className="flex items-center gap-2">
                                                                <LinkIcon className="w-4 h-4" />
                                                                <a 
                                                                    href={event.event_url} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="text-blue-600 hover:text-blue-800"
                                                                >
                                                                    Join Meeting
                                                                </a>
                                                            </div>
                                                        )}
                                                        
                                                        {event.event_type === 'On-site' && event.event_location && (
                                                            <div className="flex items-center gap-2">
                                                                <MapPinIcon className="w-4 h-4" />
                                                                <span>{event.event_location}</span>
                                                            </div>
                                                        )}
                                                        
                                                        {event.notes && (
                                                            <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                                                                <strong>Notes:</strong> {event.notes}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="flex items-center gap-2 ml-4">
                                                    <Button
                                                        type="text"
                                                        icon={<EditOutlined />}
                                                        onClick={() => handleEditEvent(event)}
                                                        size="small"
                                                    >
                                                        Edit
                                                    </Button>
                                                    {event.event_type === 'On-site' && (
                                                        <Button
                                                            type="primary"
                                                            loading={gatePassLoading}
                                                            onClick={() => openGatePassModal(event.id)}
                                                            size="small"
                                                        >
                                                            Generate Gate Pass
                                                        </Button>
                                                    )}
                                                    <Popconfirm
                                                        title="Delete Event"
                                                        description="Are you sure you want to delete this event?"
                                                        onConfirm={() => handleDeleteEvent(event.id)}
                                                        okText="Yes"
                                                        cancelText="No"
                                                    >
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            size="small"
                                                        >
                                                            Delete
                                                        </Button>
                                                    </Popconfirm>
                                                </div>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-600">No events scheduled yet</p>
                                    <Button 
                                        type="primary" 
                                        onClick={() => setEventModalVisible(true)}
                                        className="mt-2"
                                    >
                                        Schedule First Event
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Resume Preview */}
                    

                    {renderResumePreview()}
                </div>
            </div>

            {/* Modals */}
            {recruiterId && candidate && (
                <>
                    <ScheduleEventModal
                        visible={eventModalVisible}
                        onCancel={handleEventScheduled}
                        applicationId={candidate.applicationId}
                        recruiterId={recruiterId}
                        candidateEmail={candidate.email}
                    />
                    
                    {selectedEvent && (
                        <EditEventModal
                            visible={editEventModalVisible}
                            onCancel={handleEventUpdated}
                            event={selectedEvent}
                            recruiterId={recruiterId}
                        />
                    )}
                </>
            )}

            {/* Gate Pass Modal */}
            <Modal
                title="Gate Pass"
                open={gatePassVisible}
                onCancel={() => setGatePassVisible(false)}
                width={900}
                footer={[
                    <Button key="close" onClick={() => setGatePassVisible(false)}>Close</Button>,
                    <Button key="print" type="primary" onClick={handlePrintGatePass}>Print</Button>
                ]}
            >
                {gatePassHtml ? (
                    <iframe
                        id="gatepass-frame"
                        title="Gate Pass"
                        srcDoc={gatePassHtml}
                        style={{ width: '100%', height: '70vh', border: '1px solid #eee' }}
                    />
                ) : (
                    <div style={{ padding: '16px' }}>Loading...</div>
                )}
            </Modal>

            <Footer />
        </div>
    );
};

export default CandidateDetails;